import React, { useRef, useState } from 'react'
import styles from './ChangePasswordModal.module.scss'
import api from '../../api'
import Input from '../Input/Input'
import Button from '../Button/Button'
import { useClickOutside } from '../../hooks/useClickOutside'
import Modal from '../Modal/Modal'

type Props = {
  onClose: () => void
}

const ChangePasswordModal: React.FC<Props> = ({ onClose }) => {
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | undefined>()
  const [success, setSuccess] = useState(false)
  const [changePassword, { isLoading }] = api.useChangePasswordMutation()
  const ref = useRef<HTMLDivElement>(null)

  useClickOutside(() => onClose(), ref)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(undefined)

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError('All fields are required')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long')
      return
    }

    try {
      const result = await changePassword({
        old_password: oldPassword,
        new_password: newPassword,
      }).unwrap()

      if (result && 'error' in (result as any)) {
        setError((result as any).error)
      } else {
        setSuccess(true)
        setTimeout(() => {
          onClose()
        }, 2000)
      }
    } catch (err: any) {
      setError(err?.data?.error || 'Failed to change password')
    }
  }

  return (
    <Modal onCloseModal={onClose}>
      <div className={styles.form} ref={ref}>
        <h2 className={styles.title}>Change Password</h2>
        <form onSubmit={handleSubmit}>
          <div className={styles.inputGroup}>
            <Input
              label="Old Password"
              type="password"
              value={oldPassword}
              onChange={(v) => setOldPassword(v as string)}
              placeholder="Enter old password"
            />
          </div>
          <div className={styles.inputGroup}>
            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(v) => setNewPassword(v as string)}
              placeholder="Enter new password"
            />
          </div>
          <div className={styles.inputGroup}>
            <Input
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(v) => setConfirmPassword(v as string)}
              placeholder="Confirm new password"
            />
          </div>

          {error && <div className={styles.errorMessage}>{error}</div>}
          {success && <div className={styles.successMessage}>Password changed successfully!</div>}

          <div className={styles.actions}>
            <Button type="button" onClick={onClose} className={styles.cancelBtn} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" className={styles.submitBtn} disabled={isLoading || success}>
              {isLoading ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default ChangePasswordModal
